<template>
  <div>
    <div
      class="flex flex-col md:flex-row md:justify-center gap-2 md:gap-4 mb-2 md:mb-6"
    >
      <div class="relative md:w-40">
        <select
          v-model="selectedMonth"
          name="month"
          class="text-sm md:text-base w-full text-left pl-6 pr-4 py-2 bg-white border border-surface-grey rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none"
        >
          <option v-for="(month, index) in months" :key="index" :value="index">
            {{ month }}
          </option>
        </select>
        <DropdownIcon
          class="absolute w-3 h-3 right-4 top-1/2 transform -translate-y-1/2 text-text pointer-events-none"
        />
      </div>

      <div class="relative md:w-40">
        <select
          name="year"
          v-model="selectedYear"
          class="text-sm md:text-base w-full text-left pl-6 pr-4 py-2 bg-white border border-surface-grey rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none"
        >
          <option v-for="year in years" :key="year" :value="year">
            {{ year }}
          </option>
        </select>

        <DropdownIcon
          class="absolute w-3 h-3 right-4 top-1/2 transform -translate-y-1/2 text-text pointer-events-none"
        />
      </div>
    </div>

    <div>
      <div class="grid grid-cols-7 gap-1 mb-2 md:mb-4">
        <div
          v-for="day in dayHeaders"
          :key="day"
          class="text-center text-sm font-semibold text-text py-2"
        >
          {{ day }}
        </div>
      </div>

      <div class="grid grid-cols-7 gap-2">
        <button
          v-for="date in calendarDates"
          :key="`${date.month}-${date.day}`"
          type="button"
          @click="selectDate(date)"
          :disabled="!date.isCurrentMonth || !date.isAvailable"
          :class="[
            'w-8 h-8 md:h-10 md:w-10 rounded-full text-xs md:text-sm font-semibold transition-colors relative',
            {
              'opacity-25': !date.isCurrentMonth,
              'opacity-50 bg-white border border-surface-grey cursor-not-allowed':
                !date.isAvailable && date.isCurrentMonth,
              'bg-white border border-surface-grey':
                date.isCurrentMonth && date.isAvailable && !date.isSelected,
              'border-primary border-3 bg-white': date.isSelected,
              'cursor-pointer hover:border-primary':
                date.isCurrentMonth && date.isAvailable && !date.isSelected,
            },
          ]"
        >
          <span
            v-if="date.isCurrentMonth && !date.isAvailable"
            class="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <span class="block w-3/5 h-px bg-text rotate-45"></span> </span
          >{{ date.day }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  /**
   * BaseDatePicker Component
   * 
   * This component provides a date picker UI with the following features:
   * - Allows users to select a date from a calendar view.
   * - Supports restricting selectable dates using `minDate`, `maxDate`, and `availableDates` props.
   * - Emits the selected date as a Unix timestamp (in seconds) via the `update:modelValue` event.
   * - Automatically adjusts the calendar view based on the selected month and year.
   * - props must be "safetickettime" which is a unix timestamp with danish GMT offset (+1) applied. If this timestamp is converted to a Date the "UTC" values of the Date object is accually danish time, and it is these valiues that should be used for display. For internal dates (like figuring out which dates to show in a month) use whatever is easier. If this becomes unbearable install luxon and rewrite the whole thing.
   * - Selected date is in local time.
   * 
   * Props:
   * - `availableDates` (number[]): Array of Unix timestamps (in seconds) representing selectable dates.
   * - `minDate` (Date): The earliest selectable date.
   * - `maxDate` (Date): The latest selectable date.
   * 
   * Emits:
   * - `update:modelValue`: Emits the selected date as a Unix timestamp (or `null` if no date is selected).
   */
  import { computed, type Ref, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import DropdownIcon from '@/components/icons/DropdownIcon.vue';

  import { useInput } from './inputs/InputMixin';

  const { t } = useI18n();

  interface CalendarDate {
    day: number;
    month: number;
    year: number;
    isCurrentMonth?: boolean;
    isAvailable?: boolean;
    isSelected?: boolean | null;
  }

  const props = defineProps<{
    availableDates?: number[];
    minDate?: Date; // notice this must be "safetickettime"
    maxDate?: Date; // notice this must be "safetickettime"
  }>();

  const emit = defineEmits<{
    'update:modelValue': [value: number | null];
  }>();

  const selectedMonth = ref(new Date().getMonth());
  const selectedYear = ref(new Date().getFullYear());
  const selectedDate: Ref<CalendarDate | null> = ref(null);

  const { currentValue } = useInput<number | null>(
    {
      modelValue: undefined,
      defaultValue: null,
      name: '',
      id: '',
    },
    (event: string, value: number | null) => emit('update:modelValue', value)
  );

  watch(currentValue, val => {
    if (typeof val === 'number' && !isNaN(val)) {
      const d = new Date(val * 1000);
      selectedDate.value = {
        day: d.getDate(),
        month: d.getMonth(),
        year: d.getFullYear(),
      };
      selectedMonth.value = d.getMonth();
      selectedYear.value = d.getFullYear();
    } else {
      selectedDate.value = null;
    }
  });

  const months = [
    t('date.january'),
    t('date.february'),
    t('date.march'),
    t('date.april'),
    t('date.may'),
    t('date.june'),
    t('date.july'),
    t('date.august'),
    t('date.september'),
    t('date.october'),
    t('date.november'),
    t('date.december'),
  ];

  const dayHeaders = [
    t('date.mon'),
    t('date.tue'),
    t('date.wed'),
    t('date.thu'),
    t('date.fri'),
    t('date.sat'),
    t('date.sun'),
  ];

  const currentYear = new Date().getFullYear();
  const minYear = computed(() =>
    props.minDate ? props.minDate.getFullYear() : currentYear - 100
  );
  const maxYear = computed(() =>
    props.maxDate ? props.maxDate.getFullYear() : currentYear + 100
  );
  const years = computed(() =>
    Array.from(
      { length: maxYear.value - minYear.value + 1 },
      (_, i) => minYear.value + i
    )
  );

  const calendarDates = computed(() => {
    const dates: CalendarDate[] = [];
    const firstDay = new Date(selectedYear.value, selectedMonth.value, 1);
    const lastDay = new Date(selectedYear.value, selectedMonth.value + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startDay = firstDay.getDay();
    startDay = startDay === 0 ? 6 : startDay - 1;

    const prevMonth = new Date(selectedYear.value, selectedMonth.value, 0);
    const prevMonthDays = prevMonth.getDate();

    for (let i = startDay - 1; i >= 0; i--) {
      dates.push({
        day: prevMonthDays - i,
        month: selectedMonth.value - 1,
        year: selectedYear.value,
        isCurrentMonth: false,
        isAvailable: false,
        isSelected: false,
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isAvailable = isDateAvailable(
        day,
        selectedMonth.value,
        selectedYear.value
      );
      const isSelected =
        selectedDate.value &&
        selectedDate.value.day === day &&
        selectedDate.value.month === selectedMonth.value &&
        selectedDate.value.year === selectedYear.value;

      dates.push({
        day,
        month: selectedMonth.value,
        year: selectedYear.value,
        isCurrentMonth: true,
        isAvailable,
        isSelected,
      });
    }

    const remainingCells = 42 - dates.length;
    for (let day = 1; day <= remainingCells; day++) {
      dates.push({
        day,
        month: selectedMonth.value + 1,
        year: selectedYear.value,
        isCurrentMonth: false,
        isAvailable: false,
        isSelected: false,
      });
    }

    return dates;
  });

  // Remember to read comments at the top regarding safetickettime.
  function isDateAvailable(day: number, month: number, year: number): boolean {    
    // const date = new Date(year, month, day);
    // date.setHours(0, 0, 0, 0);
    
    if (props.minDate && compareDates(props.minDate, year, month, day) > 0) return false;
    if (props.maxDate && compareDates(props.maxDate, year, month, day) < 0) return false;
    
    if (props.availableDates && props.availableDates.length > 0) {
      return props.availableDates.some(ts => {
        const d = new Date(ts * 1000);
        
        return d.getUTCFullYear() === year && d.getUTCMonth() === month && d.getUTCDate() === day;

        // Alternate approach: 
        // d.setUTCHours(0, 0, 0, 0); // Set UTC time to get back to the intended date regardless of timezone
        // d.setHours(0, 0, 0, 0); // set local time to midnight to compare just the date part
        // return d.getTime() === date.getTime();
      });
    }

    return true;
  }

  function selectDate(date: CalendarDate) {
    if (date.isCurrentMonth && date.isAvailable) {
      // If already selected, deselect
      if (
        selectedDate.value &&
        selectedDate.value.day === date.day &&
        selectedDate.value.month === date.month &&
        selectedDate.value.year === date.year
      ) {
        selectedDate.value = null;
        emit('update:modelValue', null);
        return;
      }
      
      selectedDate.value = {
        day: date.day,
        month: date.month,
        year: date.year,
      };
        // Math.floor(new Date(date.year, date.month, date.day).getTime() / 1000)
      emit(
        'update:modelValue',
        Math.floor(new Date(Date.UTC(date.year, date.month, date.day)).getTime() / 1000)
      );
    }
  }

  watch([selectedMonth, selectedYear], () => {
    if (selectedDate.value) {
      const { day, month, year } = selectedDate.value;
      if (month !== selectedMonth.value || year !== selectedYear.value) {
        selectedDate.value = null;
      } else if (!isDateAvailable(day, month, year)) {
        selectedDate.value = null;
      }
    }
  });

  // sftDate safetickettime
  function compareDates(sftDate: Date, year: number, month: number, day: number): number {   
    if (sftDate.getUTCFullYear() > year) {
      return 1;
    } else if (sftDate.getUTCFullYear() < year) {
      return -1;
    }

    if (sftDate.getUTCMonth() > month) {
      return 1;
    } else if (sftDate.getUTCMonth() < month) {
      return -1;
    }

    if (sftDate.getUTCDate() > day) {
      return 1;
    } else if (sftDate.getUTCDate() < day) {
      return -1;
    }

    return 0;
  }
</script>
